import React from "react";
import { ContentContainer } from "../../../components/layout";
import {
  Button,
  CustomFilePicker,
  Modal,
  Select,
  Table,
  Tooltip,
} from "../../../components/core";
import Papa from "papaparse";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  BRANCHES,
  PARTNERS,
} from "../../../constants/graphql/partner-agent-queries";
import { BATCH_UPLOAD } from "../../../constants/graphql/batch-upload-queries";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";

const HEADERS = [
  { header_key: "firstName", header_name: "First Name" },
  { header_key: "middleName", header_name: "Middle Name" },
  { header_key: "lastName", header_name: "Last Name" },
  { header_key: "suffixName", header_name: "Suffix Name" },
  { header_key: "agentMobile", header_name: "Agent Mobile" },
  { header_key: "agentEmail", header_name: "Agent Email" },
  { header_key: "birthDate", header_name: "Birth Date" },
  { header_key: "agentStatus", header_name: "Agent Status" },
  { header_key: "verifiedStatus", header_name: "Verified Status" },
  { header_key: "partnerId", header_name: "Partner" },
  { header_key: "partnerBranchId", header_name: "Branch" },
  { header_key: "loadBalance", header_name: "Load Balance" },
];

const AGENT_STATUS = ["Suspended", "Active"];
const VERIFIED_STATUS = ["Unverified", "Verified"];

function formatDate(inputDate) {
  if (!inputDate) return "";

  const options = { year: "numeric", month: "short", day: "numeric" };
  try {
    const date = new Date(inputDate.replaceAll("/", "-"));
    return date.toLocaleDateString("en-US", options);
  } catch (error) {
    return inputDate;
  }
}

// Function to validate emails
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Function to validate phone numbers
function isValidMobile(mobile) {
  // Basic Philippine mobile validation (starts with 639 and has 12 digits total)
  return /^639\d{9}$/.test(mobile);
}

export default function BatchUpload() {
  const accessPermission = useSelector(
    (state) => state.accessPermissions.accessPermissions
  );

  // Local States
  const [formData, setFormData] = React.useState({
    partnerId: "-1",
    partnerBranchId: "-1",
    verifiedStatus: "0",
    agentStatus: "1",
  });
  const [file, setFile] = React.useState(null);
  const [batchAgentData, setBatchAgentData] = React.useState([]);
  const [tableData, setTableData] = React.useState([]);
  const [previewFlag, setPreviewFlag] = React.useState(false);
  const [partnerList, setPartnerList] = React.useState([]);
  const [branchList, setBranchList] = React.useState([]);
  const [validationVisible, setValidationVisible] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [validCount, setValidCount] = React.useState(0);

  const [validation, setValidation] = React.useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  // GQL
  const partners = useQuery(PARTNERS, {
    variables: {
      operatorId: localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user")).operator_id
        : "",
    },
  });

  const branches = useQuery(BRANCHES, {
    variables: {
      operatorId: localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user")).operator_id
        : "",
      partnerId: formData.partnerId,
    },
  });

  const [batchUploadAgents] = useMutation(BATCH_UPLOAD);

  // Validate each agent entry
  const validateAgent = (agent, index) => {
    const errors = {};

    // Required fields validation
    if (!agent.firstName) errors.firstName = "Missing first name";
    if (!agent.lastName) errors.lastName = "Missing last name";

    // Email validation
    if (!agent.agentEmail) {
      errors.agentEmail = "Missing email";
    } else if (!isValidEmail(agent.agentEmail)) {
      errors.agentEmail = "Invalid email format";
    }

    // Mobile validation
    if (!agent.agentMobile) {
      errors.agentMobile = "Missing mobile number";
    } else if (!isValidMobile(agent.agentMobile)) {
      errors.agentMobile = "Invalid mobile format (should be 639xxxxxxxxx)";
    }

    // Birth date validation
    if (!agent.birthDate) {
      errors.birthDate = "Missing birth date";
    } else {
      try {
        const date = new Date(agent.birthDate.replaceAll("/", "-"));
        if (isNaN(date.getTime())) {
          errors.birthDate = "Invalid date format";
        }
      } catch (e) {
        errors.birthDate = "Invalid date format";
      }
    }

    return errors;
  };

  const csvToArrayOfObject = (convertedFile, fileExtension) => {
    if (convertedFile) {
      try {
        Papa.parse(convertedFile, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            const parsedData = result.data;

            if (fileExtension === "csv") {
              const filteredData = parsedData.filter((item) =>
                Object.values(item).some(
                  (val) => val !== undefined && val !== ""
                )
              );

              const validationResults = filteredData.map((agent, index) => {
                const errors = validateAgent(agent, index);
                return {
                  agent,
                  errors,
                  isValid: Object.keys(errors).length === 0,
                };
              });

              setValidationErrors(validationResults.map((r) => r.errors));
              setValidCount(validationResults.filter((r) => r.isValid).length);
              setBatchAgentData(filteredData);
              setValidationVisible(true);
            }
          },
        });
      } catch (e) {
        console.error("Error parsing file:", e);
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: "File Parse Error",
          message:
            "Unable to parse the uploaded file. Please check the format.",
          open: true,
        });
      }
    }
  };

  const handleFileSelected = (file) => {
    setFile(file);
  };

  // Add this function to your component
  const downloadErrorSummary = () => {
    // Create error summary text content
    let errorText = "MBOX Agent Batch Upload Error Summary\n";
    errorText += "===========================================\n\n";
    errorText += `Total Records: ${batchAgentData.length}\n`;
    errorText += `Valid Records: ${validCount}\n`;
    errorText += `Records with Errors: ${
      batchAgentData.length - validCount
    }\n\n`;

    errorText += "DETAILED ERRORS:\n";
    errorText += "===========================================\n\n";

    validationErrors.forEach((errors, rowIndex) => {
      if (Object.keys(errors).length === 0) return;

      const agent = batchAgentData[rowIndex];
      errorText += `ROW ${rowIndex + 1}:\n`;
      errorText += `Name: ${agent.firstName || "[MISSING]"} ${
        agent.lastName || "[MISSING]"
      }\n`;
      errorText += `Mobile: ${agent.agentMobile || "[MISSING]"}\n`;
      errorText += `Email: ${agent.agentEmail || "[MISSING]"}\n`;
      errorText += "Errors:\n";

      Object.entries(errors).forEach(([field, message]) => {
        errorText += `  - ${field}: ${message}\n`;
      });
      errorText += "\n";
    });

    errorText += "===========================================\n";
    errorText += "Generated on: " + new Date().toLocaleString();

    // Create and download the file
    const blob = new Blob([errorText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "batch_upload_error_summary.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOnSubmit = (e) => {
    if (formData.partnerId === "-1" || formData.partnerBranchId === "-1") {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: "Incomplete Search Criteria",
        message: "Partner and Branch are required to proceed.",
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() => setValidation({ open: false })}
              value={"OK"}
              className={"p-2 w-full"}
            />
          </div>
        ),
      });
    } else {
      if (file.name.endsWith("xlsx")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const data = event.target.result;
          const workbook = XLSX.read(data, { type: "binary" });

          // Choose first sheet
          const sheetName = workbook.SheetNames[0];
          const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]);
          csvToArrayOfObject(csv, "xlsx");
        };

        reader.readAsBinaryString(file);
      } else {
        const reader = new FileReader();
        reader.onload = (event) => {
          csvToArrayOfObject(event.target.result, "csv");
        };
        reader.readAsText(file);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const submitValidData = () => {
    // Filter out invalid entries
    const validAgents = batchAgentData.filter((agent, index) => {
      return Object.keys(validationErrors[index] || {}).length === 0;
    });

    // Prepare data for backend
    const finalParsedData = validAgents.map((agent) => ({
      ...agent,
      partnerId: formData.partnerId,
      partnerBranchId: formData.partnerBranchId,
      operatorId: JSON.parse(localStorage.getItem("user")).operator_id,
      loadBalance: 0,
      agentName: "",
      userOtp: "",
      userOtpKey: "",
      agentStatusComments: "",
      comments: "",
      password: "",
      verifiedStatus: formData.verifiedStatus,
      agentStatus: formData.agentStatus,
    }));

    // Prepare data for display table
    const tableParsedData = validAgents.map((agent) => ({
      ...agent,
      agentStatus: AGENT_STATUS[formData.agentStatus],
      verifiedStatus: VERIFIED_STATUS[formData.verifiedStatus],
      partnerId:
        partnerList.find((x) => x.value === formData.partnerId)?.name || "",
      partnerBranchId:
        branchList.find((x) => x.value === formData.partnerBranchId)?.name ||
        "",
      birthDate: formatDate(agent.birthDate),
      loadBalance: 0,
    }));

    // Check for duplicates within the file
    const mobileNumbers = finalParsedData.map((agent) => agent.agentMobile);
    const duplicates = mobileNumbers.filter(
      (number, index, array) => array.indexOf(number) !== index
    );

    if (duplicates.length > 0) {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: "Upload failed: Duplicate records in file",
        message: (
          <>
            <p className="mb-4">Duplicate record(s) found:</p>
            {duplicates.map((number, idx) => (
              <p key={idx} className="text-red-900 text-center">
                {number}
              </p>
            ))}
            <p className="mt-4 text-start">
              Please remove duplicates from file and re-upload.
            </p>
          </>
        ),
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() => {
                setValidation({ open: false });
              }}
              value={"OK"}
              className={"p-2 w-full"}
            />
          </div>
        ),
      });
      return;
    }

    // Proceed with API call
    batchUploadAgents({ variables: { agents: finalParsedData } })
      .then((data) => {
        setTableData(tableParsedData);
        setPreviewFlag(true);
        setValidationVisible(false);
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
          title: "Success",
          message: `Successfully uploaded ${tableParsedData.length} agents.`,
          open: true,
          actions: (
            <div className="w-full">
              <Button
                onClick={() => {
                  setValidation({ open: false });
                }}
                value={"OK"}
                className={"p-2 w-full"}
              />
            </div>
          ),
        });
      })
      .catch((error) => {
        const match = error.message.match(/63(.*?0\))/);
        const extractedSubstring = match ? match[0] : null;

        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: "Upload failed: Duplicate entry",
          message: extractedSubstring
            ? `The mobile number ${extractedSubstring.slice(
                0,
                -1
              )} is already in the database. Please remove or change it to proceed.`
            : "One or more mobile numbers already exist in the database. Please check your data and try again.",
          open: true,
          actions: (
            <div className="w-full">
              <Button
                onClick={() => {
                  setValidation({ open: false });
                }}
                value={"OK"}
                className={"p-2 w-full"}
              />
            </div>
          ),
        });
      });
  };

  // Helper function to determine cell class based on errors
  const getCellClass = (rowIndex, field) => {
    const errors = validationErrors[rowIndex] || {};
    return errors[field]
      ? "border p-1 sm:p-2 bg-red-100 text-red-700"
      : "border p-1 sm:p-2";
  };

  // Use effects
  React.useEffect(() => {
    if (partners.data) {
      const tempArray = partners.data.partners.map((data) => ({
        value: data.partnerId,
        name: data.companyNickname,
      }));
      setPartnerList(tempArray);
    }
  }, [partners.data]);

  React.useEffect(() => {
    if (
      branches.data &&
      formData.partnerId !== "-1" &&
      formData.partnerId !== -1
    ) {
      const tempArray = branches.data.partnerBranches.map((data) => ({
        value: data.partnerBranchId,
        name: data.partnerBranch,
      }));
      setBranchList(tempArray);
    }
  }, [branches.data]);

  // Create a URL for the sample CSV
  const createSampleCSVBlob = () => {
    const csvContent =
      "firstName,middleName,lastName,suffixName,agentMobile,agentEmail,birthDate\n" +
      "Juan,Reyes,Dela Cruz,Jr.,639171234567,juan.delacruz@example.com,1990-05-15\n" +
      "Maria,Santos,Gonzales,,639189876543,maria.gonzales@example.com,1988-11-23";

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    return URL.createObjectURL(blob);
  };

  return (
    <ContentContainer containerTitle={"Batch Upload"}>
      <p>
        To do a batch upload of new agents, you must follow MBOX Agent file
        format. Please click{" "}
        <b>
          <u>
            <a
              href={createSampleCSVBlob()}
              download="sample_batch_upload_agent.csv"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-800"
            >
              here
            </a>
          </u>
        </b>{" "}
        to download sample csv file.
      </p>
      <div className="w-4/5 grid grid-cols-5 gap-2">
        <div className="col-span-1">
          <p className="text-base mt-2">Partner: </p>
        </div>
        <div className="bg-slate-50 col-span-3">
          <Select
            disabled={
              accessPermission.batchUpload === 1 ||
              accessPermission.batchUpload === "1"
            }
            value={formData.partnerId}
            items={partnerList}
            defaultOption={{ value: "-1", name: "-- Select a Partner --" }}
            onChange={handleChange}
            name="partnerId"
          />
        </div>
        <div className="col-span-1"></div>
        {/*  */}
        <div className="col-span-1">
          <p className="text-base mt-2">Branch: </p>
        </div>
        <div className="bg-slate-50 col-span-3">
          <Select
            disabled={
              accessPermission.batchUpload === 1 ||
              accessPermission.batchUpload === "1"
            }
            value={formData.partnerBranchId}
            items={branchList}
            defaultOption={{ value: "-1", name: "-- Select a Branch --" }}
            onChange={handleChange}
            name="partnerBranchId"
            disable={formData.partnerId === "-1"}
          />
        </div>
        <div className="col-span-1 flex"></div>
        {/*  */}
        <div className="col-span-1">
          <p className="text-base mt-2">ID Verification Status: </p>
        </div>
        <div className="bg-slate-50 col-span-3">
          <Select
            disabled={
              accessPermission.batchUpload === 1 ||
              accessPermission.batchUpload === "1"
            }
            value={formData.verifiedStatus}
            onChange={handleChange}
            items={[
              { value: "0", name: "Unverified" },
              { value: "1", name: "Verified" },
            ]}
            name="verifiedStatus"
          />
        </div>
        <div className="col-span-1 flex"></div>
        {/*  */}
        <div className="col-span-1">
          <p className="text-base mt-2"> Agent Access Status: </p>
        </div>
        <div className="col-span-3">
          <Select
            disabled={
              accessPermission.batchUpload === 1 ||
              accessPermission.batchUpload === "1"
            }
            value={formData.agentStatus}
            onChange={handleChange}
            items={[
              { value: "1", name: "Active" },
              { value: "0", name: "Suspended" },
            ]}
            name="agentStatus"
          />
        </div>
        <div className="col-span-1 flex">
          <Tooltip
            message={
              <div>
                <p className="font-bold text-green-700">ACTIVE</p>
                <p className="indent-5">Set all agent's status to active.</p>
                <div className="my-4"></div>
                <p className="font-bold text-red-700">SUSPENDED</p>
                <p className="indent-5">Set agent status to suspended.</p>
              </div>
            }
          />
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <CustomFilePicker
          disabled={
            accessPermission.batchUpload === 1 ||
            accessPermission.batchUpload === "1"
          }
          onFileSelected={handleFileSelected}
          value={"Select file to upload"}
          acceptedFileTypes={["csv", "xlsx"]}
        />
        {file && (
          <Button
            disabled={
              accessPermission.batchUpload === 1 ||
              accessPermission.batchUpload === "1"
            }
            value={"Upload agents"}
            className={"bg-primary-primaryYellow text-black px-2 py-2 mt-4"}
            onClick={() => {
              setValidation({
                icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
                title: "Batch Upload agents",
                message:
                  "Are you sure you want to upload this batch of agents?",
                open: true,
                actions: (
                  <div className="w-full grid grid-cols-2 gap-4">
                    <Button
                      onClick={() => setValidation({ open: false })}
                      value={"No"}
                      className={"p-2 w-full"}
                    />
                    <Button
                      onClick={handleOnSubmit}
                      value={"Yes"}
                      className={"p-2 w-full"}
                    />
                  </div>
                ),
              });
            }}
          />
        )}
      </div>

      {/* Data Validation Dialog */}
      <Modal
        title="Data Validation"
        className={"w-[90vw]"}
        open={validationVisible}
        icon={"/assets/images/check.png"}
        message={
          <div className="max-h-96 overflow-auto">
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="font-semibold text-blue-800">
                Data Preview:{" "}
                <span className="text-green-600">{validCount}</span> valid
                agents out of {batchAgentData.length} records
              </p>
              <p className="text-sm text-blue-700">
                Only showing rows with errors. Valid records will be uploaded.
              </p>
            </div>

            {/* Only show table if there are errors */}
            {batchAgentData.some(
              (_, idx) =>
                validationErrors[idx] &&
                Object.keys(validationErrors[idx]).length > 0
            ) && (
              <table className="w-full border text-xs sm:text-sm mb-4">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-1 sm:p-2">Row</th>
                    <th className="border p-1 sm:p-2">First Name</th>
                    <th className="border p-1 sm:p-2">Last Name</th>
                    <th className="border p-1 sm:p-2">Mobile</th>
                    <th className="border p-1 sm:p-2">Email</th>
                    <th className="border p-1 sm:p-2">Birth Date</th>
                    <th className="border p-1 sm:p-2">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {batchAgentData.map((agent, idx) => {
                    // Skip rows without errors
                    if (
                      !validationErrors[idx] ||
                      Object.keys(validationErrors[idx]).length === 0
                    )
                      return null;

                    return (
                      <tr key={idx} className="bg-red-50">
                        <td className="border p-1 sm:p-2 font-semibold">
                          {idx + 1}
                        </td>
                        <td className={getCellClass(idx, "firstName")}>
                          {agent.firstName}
                        </td>
                        <td className={getCellClass(idx, "lastName")}>
                          {agent.lastName}
                        </td>
                        <td className={getCellClass(idx, "agentMobile")}>
                          {agent.agentMobile}
                        </td>
                        <td className={getCellClass(idx, "agentEmail")}>
                          {agent.agentEmail}
                        </td>
                        <td className={getCellClass(idx, "birthDate")}>
                          {agent.birthDate}
                        </td>
                        <td className="border p-1 sm:p-2 text-red-700">
                          {Object.values(validationErrors[idx]).join(", ")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Error summary section with download button */}
            {validationErrors.some(
              (errors) => Object.keys(errors).length > 0
            ) && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-red-700">Error Summary:</h4>
                  <Button
                    onClick={() => downloadErrorSummary()}
                    value={"Download Error Summary"}
                    className={"p-1 text-sm bg-red-600 text-white"}
                  />
                </div>
                <ul className="text-xs sm:text-sm text-red-700 pl-5 list-disc">
                  {validationErrors.map((errors, rowIndex) => {
                    if (Object.keys(errors).length === 0) return null;
                    return Object.entries(errors).map(
                      ([field, message], errIdx) => (
                        <li key={`${rowIndex}-${errIdx}`}>
                          Row {rowIndex + 1}, {field}: {message}
                        </li>
                      )
                    );
                  })}
                </ul>
              </div>
            )}

            {validCount === batchAgentData.length && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                <p className="font-semibold text-green-700">
                  All records are valid! Ready to upload.
                </p>
              </div>
            )}
          </div>
        }
        actions={
          <div className="w-full grid grid-cols-2 gap-4">
            <Button
              onClick={() => setValidationVisible(false)}
              value={"Cancel"}
              className={"p-2 w-full"}
            />
            <Button
              onClick={submitValidData}
              value={`Upload ${validCount} Valid Agents`}
              className={"p-2 w-full bg-green-600 text-white"}
              disabled={validCount === 0}
            />
          </div>
        }
      />

      {/* Display uploaded data */}
      {previewFlag && (
        <>
          <div className="bg-primary-primaryYellow w-full border-stone-400 border-b-2 text-center">
            <p className="font-bold text-lg m-auto p-2">
              {tableData.length} Records Uploaded
            </p>
          </div>
          <Table headers={HEADERS} items={tableData} />
        </>
      )}

      {/* Other modals */}
      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        actions={
          validation.actions || (
            <Button
              onClick={() => setValidation({ open: false })}
              value={"OK"}
              className={"p-2 w-full"}
            />
          )
        }
        className={"overflow-y-auto"}
      />
    </ContentContainer>
  );
}
